import { RootState } from 'store/store';
import {
  Chat,
  ChatSummary,
  ChatSummaryResponse,
  ExpertRole,
  MessageQuery,
} from 'types/chat';
import {
  buildModelsFromSettings,
  createMessageClientId,
  streamEventHandler,
  streamReader,
  syncDraftWithRequest,
  transformChatResponse,
} from './utils';
import { baseApi } from 'store/api/base-api';
import { transformDate } from 'lib/utils/common';
import {
  selectChatSettings,
  selectModesAndModelsResult,
} from '../chatSettings/selectors';
import i18n from 'i18n';

export const chatApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getChats: builder.query<ChatSummary[], void>({
      query: () => ({
        url: 'getAllChats',
        method: 'GET',
      }),
      transformResponse(
        response: { chats: ChatSummaryResponse[]; projects: [] },
        _meta,
        _arg,
      ) {
        const { chats: chatsResponse } = response;

        const chats = chatsResponse.map((chat) => transformChatResponse(chat));
        return chats;
      },
      providesTags: ['Chat'],
    }),

    getChat: builder.query<Chat, string>({
      query: (chatId) => ({
        url: `getChat?chatId=${chatId}`,
        method: 'GET',
      }),
      transformResponse: transformChatResponse,
      providesTags: (result, error, chatId) =>
        result ? [{ type: 'Chat', id: chatId }] : ['Chat'],
    }),

    createChat: builder.mutation<Chat, void>({
      query: () => ({
        url: `/createAugmentedChat`,
        method: 'POST',
      }),
      transformResponse: transformChatResponse,
      async onQueryStarted(_arg, { queryFulfilled, dispatch, getState }) {
        try {
          const { data } = await queryFulfilled;

          const state = getState() as RootState;
          const settings = selectChatSettings(state);

          const modesAndModels = selectModesAndModelsResult(state).data;
          const modelNameById = new Map(
            (modesAndModels?.models ?? []).map(
              (model) => [model.id, model.name] as const,
            ),
          );

          const configuredModels = buildModelsFromSettings(
            settings.mode,
            settings.models,
            settings.mainModel,
            modelNameById,
          );

          const defaultChat: Chat = {
            ...data,
            mode: settings.mode,
            models:
              configuredModels.length > 0 ? configuredModels : data.models,
            additional: settings.additional,
            jurisdiction: data.jurisdiction, // add jurisdiction from settings
            themeOptions: settings.themeOptions,
          };

          const defaultChatSummary: ChatSummary = {
            id: defaultChat.id,
            name: defaultChat.name,
            date: defaultChat.date,
            isPinned: defaultChat.isPinned,
            projectId: defaultChat.projectId,
            mode: defaultChat.mode,
            models: defaultChat.models.map((model) => ({
              id: model.id,
              name: model.name,
              expertRole: model.expertRole,
              order: model.order,
              status: model.status,
            })),
          };

          dispatch(
            chatApi.util.upsertQueryEntries([
              {
                endpointName: 'getChat',
                arg: data.id,
                value: defaultChat,
              },
            ]),
          );

          dispatch(
            chatApi.util.updateQueryData('getChats', undefined, (draft) => {
              draft.unshift(defaultChatSummary);
            }),
          );
        } catch {
          // console.log('Ошибка создания чата:', error);
        }
      },
    }),

    renameChat: builder.mutation<
      { success: true; message: 'string' },
      { chatId: Chat['id']; name: string }
    >({
      query: ({ chatId, name }) => ({
        url: `renameChat`,
        method: 'POST',
        body: { chatId, name },
      }),
      async onQueryStarted({ chatId, name }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            chatApi.util.updateQueryData('getChats', undefined, (draft) => {
              const index = draft.findIndex((chat) => chat.id === chatId);
              if (index !== -1) {
                draft[index] = { ...draft[index], name };
              }
            }),
          );
        } catch {
          // console.log('Rename error:', err);
        }
      },
      invalidatesTags: (result, error, arg) =>
        result ? [{ type: 'Chat', id: arg.chatId }] : ['Chat'],
    }),

    deleteChat: builder.mutation<void, { chatId: Chat['id'] }>({
      query: ({ chatId }) => ({
        url: 'deleteChat',
        method: 'POST',
        body: { chatId },
      }),
      async onQueryStarted({ chatId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            chatApi.util.updateQueryData('getChats', undefined, (draft) => {
              return draft.filter((chat) => chat.id !== chatId);
            }),
          );
        } catch {
          // console.log('Delete error:', err);
        }
      },
    }),

    pinChat: builder.mutation<
      { success: true; message: 'string' },
      { chatId: Chat['id']; isPinned: Chat['isPinned'] }
    >({
      query: ({ chatId, isPinned }) => ({
        url: `pinChat`,
        method: 'POST',
        body: { chatId, isPinned },
      }),
      async onQueryStarted({ chatId, isPinned }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            chatApi.util.updateQueryData('getChats', undefined, (draft) => {
              const index = draft.findIndex((chat) => chat.id === chatId);
              if (index !== -1) {
                draft[index] = { ...draft[index], isPinned };
              }
            }),
          );
        } catch {
          // console.log('Pinned error:', err);
        }
      },
      invalidatesTags: (result, error, arg) =>
        result ? [{ type: 'Chat', id: arg.chatId }] : ['Chat'],
    }),

    moveChat: builder.mutation<
      ChatSummary,
      { chatId: Chat['id']; projectId: Chat['projectId'] }
    >({
      query: (body) => ({
        url: 'project/moveChat',
        method: 'POST',
        body,
      }),
      async onQueryStarted(body, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const { chatId, projectId } = body;
          dispatch(
            chatApi.util.updateQueryData('getChat', chatId, (draft) => {
              draft.projectId = projectId;
            }),
          );

          dispatch(
            chatApi.util.updateQueryData('getChats', undefined, (draft) => {
              const chatItem = draft.find((item) => item.id === chatId);

              if (chatItem) chatItem.projectId = projectId;
            }),
          );
        } catch {
          // console.log('Move chat err...', error);
        }
      },
    }),

    sendMessage: builder.mutation<void, MessageQuery>({
      async onQueryStarted(body, { queryFulfilled, dispatch }) {
        try {
          dispatch(
            chatApi.util.updateQueryData('getChat', body.chatId, (draft) => {
              syncDraftWithRequest(draft, body);

              const { date, time } = transformDate(new Date().toISOString());
              const common = { id: null, rating: null, date, time };
              const attachments = body.attachments.map(
                ({ filename, ...fileData }) => ({
                  fileName: filename,
                  ...fileData,
                }),
              );

              // console.log('body', body);

              draft.models.forEach((model) => {
                if (model.status === 'disabled') return;

                const lastMessage = model.messages.at(-1);

                const isNewUserMessage = body.messageId !== lastMessage?.id;
                const isAssistantLastMessage =
                  lastMessage?.role === 'assistant';
                const shouldReplacePendingUserMessage =
                  lastMessage?.role === 'user' &&
                  lastMessage.id === null &&
                  body.messageId === null;

                if (isNewUserMessage || isAssistantLastMessage) {
                  model.messages.push({
                    ...common,
                    clientId: createMessageClientId(),
                    role: 'user',
                    text: body.message,
                    attachments,
                  });
                } else if (shouldReplacePendingUserMessage) {
                  lastMessage.text = body.message;
                  lastMessage.attachments = attachments;
                  lastMessage.date = date;
                  lastMessage.time = time;
                }

                // Placeholder ассистента для конкретной модели
                const tail = model.messages.at(-1);
                const hasPendingAssistant =
                  tail?.role === 'assistant' && tail.id === null;

                if (!hasPendingAssistant) {
                  if (
                    body.expertise === 'dialogue' &&
                    model.expertRole === 'summary'
                  ) {
                    draft.models.forEach((m, i) => {
                      if (i === 0) return; // skip first summary model
                      if (m.status === 'disabled') return;

                      model.messages.push({
                        ...common,
                        clientId: createMessageClientId(),
                        role: 'assistant',
                        text: '',
                        model: {
                          id: m.id,
                          name: m.name,
                          order: m.order,
                          expertRole: m.expertRole,
                        },
                      });
                    });
                  }
                  model.messages.push({
                    // for summary model
                    ...common,
                    clientId: createMessageClientId(),
                    role: 'assistant',
                    text: '',
                    model: {
                      id: model.id,
                      name: model.name,
                      order: model.order,
                      expertRole: model.expertRole,
                    },
                  });
                }
              });
            }),
          );
          await queryFulfilled;
        } catch {
          dispatch(
            chatApi.util.updateQueryData('getChat', body.chatId, (draft) => {
              draft.models.forEach((model) => {
                const lastUserMessageIndex = model.messages.findLastIndex(
                  (message) => message.role === 'user',
                );

                if (lastUserMessageIndex === -1) return;

                model.messages = model.messages.filter((message, index) => {
                  if (index <= lastUserMessageIndex) return true;
                  return !(message.role === 'assistant' && message.id === null);
                });

                // if (model.messages.at(-1)?.id === null) model.messages.pop();
              });
            }),
          );
        }
      },

      async queryFn(body, api, _extraOptions) {
        const handler = streamEventHandler(api, body.chatId);
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}sendAugmentedMessageStream`,
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Accept-Language':
                  i18n.resolvedLanguage || i18n.language || 'en',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(body),
              signal: api.signal,
            },
          );
          if (!response.ok) {
            return {
              error: { status: response.status, data: await response.json() },
            };
          }
          await streamReader(
            response,
            (line) => {
              try {
                const event = JSON.parse(line);
                if (event.type === 'ping') return;
                handler.handleEvent(event);
              } catch {
                /* ignore parse error */
                // console.log('parse error', e);
              }
            },
            api.signal,
          );
          handler.flushPendingDeltas();

          const error = handler.getStreamError();
          if (error) return { error };
          return { data: undefined };
        } catch (e: unknown) {
          handler.flushPendingDeltas();
          const message = e instanceof Error ? e.message : 'Unknown error';
          return { error: { status: 'FETCH_ERROR', error: message } };
        } finally {
          handler.dispose();
        }
      },
    }),

    rateMessage: builder.mutation<
      void,
      { chatId: string; messageId: number; rating: 0 | 1 }
    >({
      query: (body) => ({
        url: `rate`,
        method: 'POST',
        body,
      }),
      async onQueryStarted(
        { chatId, messageId, rating },
        { dispatch, queryFulfilled },
      ) {
        try {
          await queryFulfilled;

          dispatch(
            chatApi.util.updateQueryData('getChat', chatId, (draft) => {
              if (draft.mode === 'single') {
                const targetMessage = draft.models[0].messages.find(
                  (item) => item.id === messageId,
                );

                if (targetMessage !== undefined) {
                  targetMessage.rating = rating;
                }
              }
            }),
          );
        } catch {
          // console.log('Rate error:', err);
        }
      },
    }),

    disableChatModel: builder.mutation<
      { success: true; message: string },
      { chatId: string; modeId: string; expertRole: ExpertRole }
    >({
      query: (body) => ({
        url: '/api/chat/mode/disable',
        method: 'POST',
        body: { ...body, disabled: true },
      }),
      async onQueryStarted(body, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            chatApi.util.updateQueryData('getChat', body.chatId, (draft) => {
              const targetModel = draft.models.find(
                (item) =>
                  item.id === body.modeId &&
                  item.expertRole === body.expertRole,
              );
              if (targetModel) targetModel.status = 'disabled';
            }),
          );

          dispatch(
            chatApi.util.updateQueryData('getChats', undefined, (draft) => {
              const chatItem = draft.find((item) => item.id === body.chatId);
              if (!chatItem) return;
              const targetModel = chatItem.models.find(
                (item) =>
                  item.id === body.modeId &&
                  item.expertRole === body.expertRole,
              );
              if (targetModel) targetModel.status = 'disabled';
            }),
          );
        } catch {
          // console.log('Disable model error:', err);
        }
      },
    }),

    deleteChatModel: builder.mutation<
      { success: true; message: string },
      { chatId: string; modeId: string; expertRole: ExpertRole }
    >({
      query: (body) => ({
        url: '/api/chat/mode/delete',
        method: 'POST',
        body,
      }),
      async onQueryStarted(body, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            chatApi.util.updateQueryData('getChat', body.chatId, (draft) => {
              draft.models = draft.models.filter((model) => {
                const isTargetModel =
                  model.id === body.modeId &&
                  model.expertRole === body.expertRole;

                return !isTargetModel;
              });
            }),
          );

          dispatch(
            chatApi.util.updateQueryData('getChats', undefined, (draft) => {
              const chatItem = draft.find((item) => item.id === body.chatId);
              if (!chatItem) return;
              chatItem.models = chatItem.models.filter((item) => {
                const isTargetModel =
                  item.id === body.modeId &&
                  item.expertRole === body.expertRole;

                return !isTargetModel;
              });
            }),
          );
        } catch {
          // console.log('Delete model error:', err);
        }
      },
    }),

    getAudioTranscription: builder.mutation<{ text: string }, FormData>({
      query: (formData) => ({
        url: '/api/audio/transcriptions',
        method: 'POST',
        body: formData,
        timeout: 0,
      }),
    }),
  }),
});

export const {
  useGetChatsQuery,
  useLazyGetChatQuery,
  useGetChatQuery,
  useCreateChatMutation,
  useRenameChatMutation,
  useDeleteChatMutation,
  usePinChatMutation,
  useMoveChatMutation,
  useSendMessageMutation,
  useRateMessageMutation,
  useDisableChatModelMutation,
  useDeleteChatModelMutation,
  useGetAudioTranscriptionMutation,
} = chatApi;
